import type { CaseManifestV1, LayerASpec } from "@cold-case/shared/case-manifest";
import {
  LAYER_A_GRID_H,
  LAYER_A_GRID_W,
  LAYER_A_MARGIN_TILES,
  TILE_SIZE,
} from "@cold-case/shared/constants";

import { zoneDisplayName } from "../lib/building-display.js";
import type { ZoneId } from "./map-grid.js";

export function zoneTransitDestination(current: ZoneId): ZoneId {
  return current === "downtown" ? "maple_heights" : "downtown";
}

export function zoneTransitConfirmMessage(
  manifest: CaseManifestV1,
  current: ZoneId
): string {
  const dest = zoneDisplayName(manifest, zoneTransitDestination(current));
  return `Confirm travel to ${dest}?`;
}

export type ZoneTransitTile = { tile_x: number; tile_y: number };

/** Zone-change portal on the walk strip: downtown NW, Maple Heights SE (road runs SE→NW). */
export function zoneTransitTile(
  which: ZoneId,
  layerA: LayerASpec
): ZoneTransitTile {
  const strip = which === "downtown" ? layerA.downtown_walk : layerA.suburb_walk;
  const sx = strip?.spine_tx;
  const sy = strip?.spine_ty;
  if (sx && sy && sx.length > 0 && sx.length === sy.length) {
    const idx = which === "downtown" ? sx.length - 1 : 0;
    return { tile_x: Math.trunc(sx[idx]!), tile_y: Math.trunc(sy[idx]!) };
  }
  const raw =
    which === "downtown" ? layerA.transit_downtown : layerA.transit_maple_heights;
  const ok =
    raw &&
    Number.isFinite(raw.tile_x) &&
    Number.isFinite(raw.tile_y) &&
    raw.tile_x >= 0 &&
    raw.tile_x < LAYER_A_GRID_W &&
    raw.tile_y >= 0 &&
    raw.tile_y < LAYER_A_GRID_H;
  if (ok) return { tile_x: Math.trunc(raw.tile_x), tile_y: Math.trunc(raw.tile_y) };
  return which === "downtown"
    ? {
        tile_x: LAYER_A_MARGIN_TILES + 2,
        tile_y: LAYER_A_MARGIN_TILES + 2,
      }
    : {
        tile_x: LAYER_A_GRID_W - LAYER_A_MARGIN_TILES - 2,
        tile_y: Math.floor(LAYER_A_GRID_H / 2),
      };
}

/** Chevron texture points left at rotation 0; downtown portal faces Maple Heights (west). */
export function zoneTransitArrowRotation(zone: ZoneId): number {
  return zone === "downtown" ? 0 : Math.PI;
}

/** World position of the zone transit chevron (matches GameScene transit arrow sprite). */
export function zoneTransitArrowWorldPoint(
  zone: ZoneId,
  layerA: LayerASpec
): { wx: number; wy: number } {
  const t = zoneTransitTile(zone, layerA);
  const cxBase = t.tile_x * TILE_SIZE + TILE_SIZE / 2;
  const cyBase = t.tile_y * TILE_SIZE + TILE_SIZE / 2;
  const rotation = zoneTransitArrowRotation(zone);
  const off = TILE_SIZE * 0.35;
  const pointAngle = rotation + Math.PI;
  return {
    wx: cxBase + Math.cos(pointAngle) * off,
    wy: cyBase + Math.sin(pointAngle) * off,
  };
}
